import React from 'react';
import { getRouter } from '../../../../../shared/lib/comm/gateway_ws/connection';
import { useWebSocketStatus } from '../../../../../shared/lib/comm/gateway_ws/useWebSocketStatus';
import { getChatMessages, addChatMessage, setChatMessages } from '../../chat/components/sharedChatService';
import { ChatMessage } from '../../chat/components/sharedChatService';
import { addBuildEntry } from '../../../../../shared/lib/buildStatusTracker';
import { TokenManager } from '../../../../../shared/lib/apiClient';

type ContractPayload = {
    contract: {
        metadata?: Record<string, any>;
        settings?: Record<string, any>;
        requirements?: Record<string, any>;
    };
};

interface ContractPopupModalProps {
    contractPopups: Set<string>;
    setContractPopups: React.Dispatch<React.SetStateAction<Set<string>>>;
    addTerminalLog: (message: string) => void;
    getConnectionStatus: () => { text: string; failed: boolean; canSend: boolean };
    canSendToAgent: () => boolean;
    onEpicBuildTrackingInitialized?: (tracking: { hlrUiid: string; llrUiids: string[] }) => void;
}

const ContractPopupModal: React.FC<ContractPopupModalProps> = ({
    contractPopups,
    setContractPopups,
    addTerminalLog,
    getConnectionStatus,
    canSendToAgent,
    onEpicBuildTrackingInitialized
}) => {
    const wsStatus = useWebSocketStatus();

    const handleSendContract = async () => {
        console.log('Sending contract to VishCoder...');
        addTerminalLog('🚀 Sending contract to VishCoder for processing...');
        
        const contractData = getChatMessages('requirements')
            .filter((msg: ChatMessage) => msg.isContract && contractPopups.has(msg.id))
            .map((msg: ChatMessage) => msg.contractData)[0];
        
        if (!contractData) {
            addTerminalLog('❌ No contract data found');
            return;
        }
        
        const router = getRouter();
        const comm = router.getCommunicationManager();
        
        if (comm && wsStatus.isConnected) {
            // Determine contract kind (epic or feature)
            const contractKind = contractData.contractKind || 'feature';
            const isEpic = contractKind === 'epic';
            
            // Use UIID based on contract kind
            const threadId = isEpic
                ? (contractData.requirements?.high_level_requirements?.uiid || 
                   contractData.metadata?.feature_Number || 
                   contractData.metadata?.dateTime || 
                   `epic_${Date.now()}`)
                : (contractData.requirements?.low_level_requirements?.uiid || 
                   contractData.metadata?.feature_Number || 
                   contractData.metadata?.dateTime || 
                   `contract_${Date.now()}`);
            
            // Determine channel based on contract kind
            const channel = isEpic ? 'build_epic' : 'build_feature';
            
            // Origin must include projectId, userId, featureId for backend (mandatory payload fields)
            const userId = TokenManager.getUserId() || contractData.metadata?.user_id;
            const projectIdStr = contractData.metadata?.project_id || contractData.metadata?.projectId;
            
            const messageId = crypto.randomUUID();
            const timestamp = new Date().toISOString();
            router.setChannelContext(channel, {
                threadId: threadId,
                originMessageId: messageId,
                originActor: 'user',
                origints: timestamp,
                ...(userId && { userId: String(userId) }),
                ...(projectIdStr && { projectId: String(projectIdStr) }),
                ...(threadId && { featureId: threadId })
            }, messageId, 'user', timestamp);
            
            const contractPayload: ContractPayload = {
                contract: {
                    metadata: contractData.metadata || {},
                    settings: contractData.settings || {},
                    requirements: contractData.requirements || {}
                }
            };
            
            const sent = comm.emit(channel, 'user', 'streaming', 'contract', contractPayload, 'request');
            
            if (sent) {
                // Track build status - extract UIID based on contract kind
                const trackingUiid = isEpic
                    ? contractPayload.contract.requirements?.high_level_requirements?.uiid
                    : contractPayload.contract.requirements?.low_level_requirements?.uiid;
                const projectId = contractPayload.contract.metadata?.project_id || 
                                 contractPayload.contract.metadata?.projectId ||
                                 null;
                
                if (trackingUiid && projectId) {
                    try {
                        // Try to get git commit from metadata or settings if available
                        const gitCommit = contractPayload.contract.metadata?.git_commit || 
                                         contractPayload.contract.settings?.git_commit || 
                                         null;
                        
                        if (isEpic) {
                            const hlrUiid = String(trackingUiid);
                            const lowLevelRequirements =
                                contractPayload.contract.requirements?.high_level_requirements?.low_level_requirements || {};
                            const llrUiids = Object.values(lowLevelRequirements)
                                .map((llr: any) => llr?.uiid)
                                .filter((uiid: unknown): uiid is string => typeof uiid === 'string' && uiid.trim().length > 0);

                            // Initialize HLR and all child LLRs as in-progress when epic starts.
                            addBuildEntry(projectId, hlrUiid, 'In Progress', gitCommit);
                            llrUiids.forEach((llrUiid: string) => {
                                addBuildEntry(projectId, llrUiid, 'In Progress', gitCommit);
                            });

                            onEpicBuildTrackingInitialized?.({
                                hlrUiid,
                                llrUiids
                            });

                            addTerminalLog(
                                `📊 Build tracking initialized for epic HLR ${hlrUiid} and ${llrUiids.length} LLR(s) in project: ${projectId}`
                            );
                        } else {
                            // Feature flow tracks a single LLR.
                            addBuildEntry(projectId, trackingUiid, 'In Progress', gitCommit);
                            addTerminalLog(`📊 Build tracking initialized for feature: ${trackingUiid} in project: ${projectId}`);
                        }
                    } catch (error) {
                        console.error('Failed to track build status:', error);
                        addTerminalLog('⚠️ Failed to track build status, but contract was sent successfully');
                    }
                } else {
                    if (!trackingUiid) {
                        const uiidType = isEpic ? 'high_level_requirements' : 'low_level_requirements';
                        console.warn(`⚠️ No UIID found in ${uiidType}, skipping build tracking`);
                    }
                    if (!projectId) {
                        console.warn('⚠️ No project_id found in contract metadata, skipping build tracking');
                    }
                }
                
                const confirmationMessage: ChatMessage = {
                    id: `confirmation-${Date.now()}`,
                    type: 'assistant',
                    content: '✅ Contract sent to VishCoder.',
                    timestamp: new Date(),
                    isContract: false,
                    page: 'requirements'
                };
                addChatMessage(confirmationMessage);
                const contractType = isEpic ? 'epic' : 'feature';
                addTerminalLog(`📡 ${contractType.charAt(0).toUpperCase() + contractType.slice(1)} contract payload sent via WebSocket to VishCoder (UIID: ${threadId})`);
                setContractPopups(new Set());
            } else {
                const errorMessage: ChatMessage = {
                    id: `error-${Date.now()}`,
                    type: 'assistant',
                    content: '❌ Failed to send contract: Could not send message.',
                    timestamp: new Date(),
                    isContract: false,
                    page: 'requirements'
                };
                addChatMessage(errorMessage);
                addTerminalLog('❌ Failed to send contract message');
            }
        } else {
            const errorMessage: ChatMessage = {
                id: `error-${Date.now()}`,
                type: 'assistant',
                content: '❌ Failed to send contract: Could not establish WebSocket connection to VishCoder. Please try again.',
                timestamp: new Date(),
                isContract: false,
                page: 'requirements'
            };
            addChatMessage(errorMessage);
            addTerminalLog('❌ WebSocket connection to VishCoder not available');
        }
    };

    if (contractPopups.size === 0) {
        return null;
    }

    const status = getConnectionStatus();

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-[10000] flex items-center justify-center p-4"
            onClick={() => setContractPopups(new Set())}
        >
            <div 
                className="bg-white rounded-lg shadow-2xl max-w-5xl max-h-[90vh] w-full overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="bg-gray-800 text-white px-6 py-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold">📄 Review and Edit Contract</h3>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handleSendContract}
                            disabled={!canSendToAgent()}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {status.canSend ? (
                                '🚀 Send to VishCoder'
                            ) : (
                                <span className="flex items-center space-x-2">
                                    {status.failed ? (
                                        <>
                                            <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                            <span>{status.text}</span>
                                        </>
                                    ) : (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            <span>{status.text}</span>
                                        </>
                                    )}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => setContractPopups(new Set())}
                            className="text-gray-300 hover:text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
                            title="Close contract editor"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="p-6">
                    {getChatMessages('requirements')
                        .filter((msg: ChatMessage) => msg.isContract && contractPopups.has(msg.id))
                        .map((message: ChatMessage) => (
                            <div key={message.id} className="mb-6 last:mb-0">
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <div className="mb-4">
                                        <h4 className="text-lg font-semibold text-gray-800 mb-2">
                                            Edit Contract JSON
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                            Review and modify the contract before sending to Docker. Changes will be saved automatically.
                                        </p>
                                    </div>
                                    {message.contractData && (
                                        <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                                            <textarea
                                                className="w-full h-96 bg-gray-900 text-green-400 font-mono text-sm border-none outline-none resize-none"
                                                defaultValue={JSON.stringify(message.contractData, null, 2)}
                                                onChange={(e) => {
                                                    try {
                                                        const newData = JSON.parse(e.target.value);
                                                        // Update message in shared service
                                                        const updatedMessages = getChatMessages().map((msg: ChatMessage) => 
                                                            msg.id === message.id 
                                                                ? { ...msg, contractData: newData }
                                                                : msg
                                                        );
                                                        setChatMessages(updatedMessages);
                                                    } catch (error) {
                                                        console.log('Invalid JSON input:', error);
                                                    }
                                                }}
                                                spellCheck={false}
                                                placeholder="Edit your contract JSON here..."
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                </div>
            </div>
        </div>
    );
};

export default ContractPopupModal;
